import { useCallback, useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import {
  deriveBlocks,
  isPlaced as isBlockPlaced,
  togglePlacement,
  type ComposedBlock,
  type Draft,
} from "@jackedlogs/shared";

import type { GridPlacement } from "../../IntensityGrid/IntensityGridView";

// PE-22 K2.3 — the block-placement seam. The grid's `placement` prop is
// block-shaped (`ComposedBlock`); there is no slot-level placement
// primitive (SCOUT confirmed). This resolves the card's FOCUSED exercise
// (by exerciseId/slug) to its `ComposedBlock[]` via the PURE
// `deriveBlocks`/`isPlaced`/`togglePlacement` — the same functions
// `useEditInPlace.ts:200-233` composes over for the existing composer, but
// matched by a raw slug instead of `useComposer`'s stateful
// `ComposerEntry`/`blocks` lens. ⭐ That lens is deliberately NOT adopted
// here — the card's cell writes stay slot-direct (K2.2's draft-direct
// adapter); this hook ONLY answers "is this exercise placed on (day,
// week)" and writes the toggle, both pure `Draft -> Draft` functions.
//
// `activeExerciseId === null` (no exercise focused) returns `null` — the
// matrix is navigation-only then, exactly like the composer.
export function usePrescribePlacement(
  programDraft: Draft,
  setDraft: Dispatch<SetStateAction<Draft>>,
  activeExerciseId: string | null,
  label: string,
): GridPlacement | null {
  const blocks = useMemo<ComposedBlock[]>(() => {
    if (!activeExerciseId) return [];
    return deriveBlocks(programDraft).filter(
      (b): b is ComposedBlock =>
        b.kind === "composed" &&
        b.members.some((m) => m.exerciseId === activeExerciseId),
    );
  }, [programDraft, activeExerciseId]);

  // Any of the focused exercise's blocks placed at (day, week) — a slot can
  // legitimately span more than one block once weeks diverge (the same
  // reason useEditInPlace's `isPlacedAt` checks `openBlocks().some(...)`).
  const isPlaced = useCallback(
    (dayIdx: number, week: number) =>
      blocks.some((b) => isBlockPlaced(b, dayIdx, week)),
    [blocks],
  );

  // The block that owns this week, else the first — same rule
  // `useEditInPlace.ts`'s `togglePlacementAt` uses: a brand-new cell places
  // against the entry's first block, an already-diverged week toggles the
  // block that already occupies it.
  const onToggle = useCallback(
    (dayIdx: number, week: number) => {
      const block = blocks.find((b) => b.weeks.includes(week)) ?? blocks[0];
      if (!block) return;
      setDraft((d) => togglePlacement(d, block, dayIdx, week));
    },
    [blocks, setDraft],
  );

  if (!activeExerciseId || blocks.length === 0) return null;
  return { label, isPlaced, onToggle };
}
